/**
 * OfflineIndicator - Shows offline status and syncs when back online
 * Also handles service worker registration
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';

// Register service worker
// GUARD: Only register in production. In development the webpack-dev-server
// hot-reload keeps re-installing the SW, which historically caused stale
// caches + reload loops. `index.js` already enforces this rule — we mirror
// it here so any component that calls `registerServiceWorker()` inherits
// the same behaviour.
export const registerServiceWorker = async () => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[SW] Skipping registration — non-production build');
    return null;
  }
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('ServiceWorker registered:', registration.scope);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            toast.info('New version available! Refresh to update.', {
              action: {
                label: 'Refresh',
                onClick: () => window.location.reload()
              }
            });
          }
        });
      });
      
      return registration;
    } catch (error) {
      console.error('ServiceWorker registration failed:', error);
    }
  }
  return null;
};

// Unregister service worker (for debugging)
export const unregisterServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.unregister();
    console.log('ServiceWorker unregistered');
  }
};

// Clear all caches
export const clearServiceWorkerCache = () => {
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage('clearCache');
    toast.success('Cache cleared');
  }
};

// Helper to get pending sync count
const getPendingSyncCount = () => {
  try {
    const pending = localStorage.getItem('eventflow_pending_sync');
    if (pending) {
      const items = JSON.parse(pending);
      return items.length;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return 0;
};

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(false);
  // Initialize pending sync count from localStorage
  const [pendingSync, setPendingSync] = useState(() => getPendingSyncCount());
  const isMounted = useRef(true);

  // Sync pending actions - memoized with useCallback
  const syncPendingActions = useCallback(async () => {
    const pending = localStorage.getItem('eventflow_pending_sync');
    if (!pending) return;

    try {
      const items = JSON.parse(pending);
      for (const item of items) {
        try {
          await fetch(item.url, {
            method: item.method,
            headers: item.headers,
            body: item.body
          });
        } catch (e) {
          console.error('Failed to sync:', item);
        }
      }
      
      localStorage.removeItem('eventflow_pending_sync');
      if (isMounted.current) {
        setPendingSync(0);
      }
      toast.success('All pending actions synced!');
    } catch (e) {
      console.error('Sync failed:', e);
    }
  }, []);

  // Register service worker once on mount
  useEffect(() => {
    registerServiceWorker();
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Back online! Syncing data...', {
        icon: <Wifi className="h-4 w-4 text-green-500" />
      });
      // Check and sync pending items
      const count = getPendingSyncCount();
      if (count > 0) {
        syncPendingActions();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
      toast.warning('You are offline. Some features may be limited.', {
        icon: <WifiOff className="h-4 w-4 text-amber-500" />,
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingActions]);

  // Don't render if online and no pending sync
  if (isOnline && pendingSync === 0) {
    return null;
  }

  return (
    <>
      {/* Floating offline indicator */}
      {!isOnline && (
        <div className="fixed bottom-4 left-4 z-50 animate-in slide-in-from-bottom">
          <div className="bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
            <WifiOff className="h-4 w-4" />
            <span className="text-sm font-medium">Offline Mode</span>
          </div>
        </div>
      )}

      {/* Pending sync indicator */}
      {pendingSync > 0 && isOnline && (
        <div className="fixed bottom-4 left-4 z-50 animate-in slide-in-from-bottom">
          <Button
            size="sm"
            className="bg-blue-500 hover:bg-blue-600 shadow-lg"
            onClick={syncPendingActions}
          >
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Syncing {pendingSync} items...
          </Button>
        </div>
      )}

      {/* Offline banner */}
      {showBanner && !isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-2 px-4 text-center z-50">
          <div className="flex items-center justify-center gap-2">
            <CloudOff className="h-4 w-4" />
            <span className="text-sm">
              You&apos;re offline. Data shown may be outdated. Changes will sync when you&apos;re back online.
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-amber-600 ml-2"
              onClick={() => setShowBanner(false)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// Hook to check online status
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Queue an action for offline sync
export const queueOfflineAction = (url, method, headers, body) => {
  const pending = localStorage.getItem('eventflow_pending_sync');
  const items = pending ? JSON.parse(pending) : [];
  
  items.push({
    id: Date.now(),
    url,
    method,
    headers,
    body,
    createdAt: new Date().toISOString()
  });
  
  localStorage.setItem('eventflow_pending_sync', JSON.stringify(items));
  return items.length;
};

export default OfflineIndicator;
